import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import CryptoJS from "crypto-js";
import {
  getAccessToken,
  setAccessToken,
  clearAccessToken,
} from "../state/tokenStore";
import logger from "../utils/logger";

const BASE = import.meta.env.VITE_APP_SERVER || "http://localhost:4000";
const KEY_HEX = import.meta.env.VITE_PUBLIC_ENCRYPTION_KEY as string;

const key = CryptoJS.enc.Hex.parse(KEY_HEX);

function encryptPayload(data: unknown): string {
  const iv = CryptoJS.lib.WordArray.random(16);
  const plaintext = CryptoJS.enc.Utf8.parse(JSON.stringify(data));
  const encrypted = CryptoJS.AES.encrypt(plaintext, key, {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });
  const ivHex = iv.toString(CryptoJS.enc.Hex);
  const ctHex = encrypted.ciphertext.toString(CryptoJS.enc.Hex);
  return `${ivHex}:${ctHex}`;
}

function decryptPayload<T = unknown>(payload: string): T {
  if (!payload) {
    logger.log("no payload");
    return {} as T;
  }
  const [ivHex, ctHex] = payload.split(":");
  const iv = CryptoJS.enc.Hex.parse(ivHex);
  const cipherParams = CryptoJS.lib.CipherParams.create({
    ciphertext: CryptoJS.enc.Hex.parse(ctHex),
  });
  const decrypted = CryptoJS.AES.decrypt(cipherParams, key, {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });
  const utf8 = decrypted.toString(CryptoJS.enc.Utf8);
  return JSON.parse(utf8) as T;
}

const api: AxiosInstance = axios.create({
  baseURL: BASE,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

let isRefreshing = false;
let queue: { resolve: (val?: any) => void; reject: (err?: any) => void }[] = [];
const processQueue = (err: any, token: string | null = null) => {
  queue.forEach((p) => (err ? p.reject(err) : p.resolve(token)));
  queue = [];
};

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken();

    // Add Bearer token
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Encrypt payload
    if (config.data !== undefined && config.headers) {
      config.headers["x-encrypted"] = "1";
      config.data = encryptPayload(config.data);
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Decrypt incoming bodies
api.interceptors.response.use(
  (res: AxiosResponse) => {
    const maybeEncrypted =
      (res.data && typeof res.data === "string") ||
      res.headers["x-encrypted"] === "1";
    if (maybeEncrypted) {
      try {
        res.data = decryptPayload(res.data);
      } catch (e) {
        console.error("Response decrypt failed:", e);
      }
    }
    return res;
  },
  async (err) => {
    const original = err.config;
    if (!original) throw err;
    if (original?.url?.includes("/auth/check-session")) {
      try {
        const error = decryptPayload<any>(err.response?.data);

        if (error?.forceLogout) {
          window.dispatchEvent(
            new CustomEvent("force-logout", {
              detail: error.message,
            }),
          );
        }

        throw error;
      } catch (e) {
        return Promise.reject(e);
      }
    }
    const error = decryptPayload<any>(err.response?.data);
    if (error?.forceLogout) {
      window.dispatchEvent(
        new CustomEvent("force-logout", {
          detail: error.message,
        }),
      );

      throw error;
    }

    if (original?.url?.includes("/auth/me")) {
      clearAccessToken();
      throw error;
    }

    if (err.response?.status === 401 && !original._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          queue.push({
            resolve: (token?: string) => {
              if (!token) return reject(new Error("Refresh failed"));
              original.headers["Authorization"] = `Bearer ${token}`;
              resolve(axios(original));
            },
            reject,
          });
        });
      }
      original._retry = true;
      isRefreshing = true;
      try {
        const refreshRes = await axios.post(
          `${BASE}/api/auth/refresh`,
          {},
          { withCredentials: true },
        );
        const newToken = refreshRes.data?.accessToken;
        if (!newToken) throw new Error("No access token from refresh");
        setAccessToken(newToken);
        original.headers["Authorization"] = `Bearer ${newToken}`;
        processQueue(null, newToken);
        return axios(original);
      } catch (e) {
        processQueue(e, null);
        clearAccessToken();
        throw e;
      } finally {
        isRefreshing = false;
      }
    }
    throw error;
  },
);

export default api;
